import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import styled from 'styled-components';

import ICOMHeaderLogo from './components/ICOMHeaderLogo';
import LanguageDropdown from './components/LanguageDropdown';
import ThemeToggle from './components/ThemeToggle';
import Navigation from './components/Navigation';
import UserMenu from './components/UserMenu';
import MobileNavDrawer from './components/MobileNavDrawer';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ToastProvider } from './contexts/ToastContext';
import { UserRole, isAdminUser } from './types/auth';
import Dashboard from './pages/Dashboard';
import InstitutionDashboard from './pages/InstitutionDashboard';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import Register from './pages/Register';
import RegisterIndividual from './pages/RegisterIndividual';
import ProfileEdit from './pages/ProfileEdit';
import ErrorBoundary from './components/common/ErrorBoundary';
import RegisterInstitution from './pages/RegisterInstitution';
import RegisterExistingMember from './pages/RegisterExistingMember';
import RegisterExistingInstitution from './pages/RegisterExistingInstitution';
import RegisterSuccess from './pages/RegisterSuccess';
import ApplicationStatus from './pages/ApplicationStatus';
import EmailConfirmation from './pages/EmailConfirmation';
import EmailCampaign from './pages/admin/EmailCampaign';
import EmailCampaignHistory from './pages/admin/EmailCampaignHistory';
import ExcelImport from './pages/admin/ExcelImport';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminMembersList from './pages/admin/AdminMembersList';
import AdminInstitutionsList from './pages/admin/AdminInstitutionsList';
import AdminChangeRequests from './pages/admin/ChangeRequests';
import UserChangeRequests from './pages/ChangeRequests';
import AdminRegistrationPetitions from './pages/admin/AdminRegistrationPetitions';
import AdminRegistrationPetitionDetail from './pages/admin/AdminRegistrationPetitionDetail';
import MemberProfile from './pages/MemberProfile';
import AdminEditMember from './pages/admin/AdminEditMember';
import InstitutionProfileView from './pages/InstitutionProfileView';
import InstitutionProfile from './pages/InstitutionProfile';
import Payments from './pages/Payments';
import PaymentResult from './pages/PaymentResult';
import Petitions from './pages/Petitions';
import Documents from './pages/Documents';
import AdminPayments from './pages/admin/AdminPayments';
import AdminVignettes from './pages/admin/AdminVignettes';
import LoadingSpinner from './components/common/LoadingSpinner';
import { Container } from './design-system/components/Layout';
import { shadows } from './design-system/tokens/shadows';
import { spacing } from './design-system/tokens/spacing';
import { borders } from './design-system/tokens/borders';
import { breakpoints } from './design-system/tokens/breakpoints';
import { hexToRgba } from './utils/colorUtils';

import './App.css';

const AppContainer = styled.div`
  margin: 0;
  padding: 0;
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  font-family: ${props => props.theme.typography?.fontFamily?.primary || 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'};
  position: relative;
  transition: background 0.3s ease;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 1000;
  flex-shrink: 0;
  overflow: visible;
  background: ${props => {
    const color = props.theme.colors.surface;
    // Enhanced glass morphism effect
    if (color.includes('rgb') || color.includes('rgba')) {
      return color.replace(')', ', 0.92)').replace('rgb', 'rgba');
    }
    return hexToRgba(color, 0.92);
  }};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${shadows.sm};
  backdrop-filter: blur(12px) saturate(150%);
  -webkit-backdrop-filter: blur(12px) saturate(150%);

  &::after {
    content: '';
    display: block;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${props => props.theme.colors.primary} 0%,
      ${props => props.theme.colors.accent} 55%,
      ${props => hexToRgba(props.theme.colors.accent, 0.2)} 100%
    );
  }
`;

const HeaderContent = styled(Container)`
  display: grid;
  grid-template-columns: minmax(200px, 232px) minmax(0, 1fr) auto;
  align-items: center;
  min-height: 72px;
  padding: 0 ${spacing[5]};
  width: 100%;
  max-width: calc(232px + 1180px);
  gap: ${spacing[4]};
  overflow: visible;

  @media (max-width: ${breakpoints.lg}) {
    display: flex;
    justify-content: space-between;
    gap: ${spacing[2]};
    max-width: none;
    padding: 0 ${spacing[3]};
  }
`;

const LogoRegion = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 72px;
  min-width: 0;
  padding-left: ${spacing[1]};

  @media (max-width: ${breakpoints.lg}) {
    gap: ${spacing[2]};
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    padding-left: 0;
  }
`;

const HeaderBrand = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  padding-right: ${spacing[3]};

  @media (max-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const HeaderBrandTitle = styled.p`
  margin: 0;
  font-size: ${props => props.theme.typography?.fontSize?.base || '1rem'};
  font-weight: 650;
  line-height: 1.25;
  color: ${props => props.theme.colors.text};
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderBrandSubtitle = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.35;
  color: ${props => props.theme.colors.textSecondary};
  white-space: normal;
  overflow-wrap: break-word;
  max-width: min(52ch, 100%);
`;

const HeaderToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing[3]};
  position: relative;
  z-index: 2;
  overflow: visible;
  flex-shrink: 0;

  @media (max-width: ${breakpoints.lg}) {
    width: auto;
    gap: ${spacing[2]};
  }
`;

const ToolbarCluster = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const BodyLayout = styled.div`
  display: flex;
  flex: 1;
  min-height: calc(100vh - 75px);
  overflow: hidden;
`;

const Sidebar = styled.aside`
  width: 232px;
  flex-shrink: 0;
  border-right: 1px solid ${props => props.theme.colors.border};
  background: ${props => {
    const surface = props.theme.colors.surface;
    const bg = props.theme.colors.background;
    return `linear-gradient(180deg, ${surface} 0%, ${hexToRgba(bg, 0.65)} 100%)`;
  }};
  overflow-y: auto;

  @media (max-width: ${breakpoints.lg}) {
    display: none;
  }
`;
const MenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
  min-width: 44px;
  min-height: 44px;
  padding: 0;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  @media (max-width: ${breakpoints.lg}) {
    display: inline-flex;
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const MainContent = styled.main`
  flex: 1;
  width: 100%;
  max-width: 1480px;
  margin: 0 auto;
  padding: ${spacing[8]} ${spacing[6]};
  min-height: calc(100vh - 70px);
  position: relative;
  overflow-y: auto;
  overflow-x: auto;
  min-width: 0;

  @media (min-width: ${breakpoints.md}) {
    padding: ${spacing[8]} ${spacing[8]};
  }

  &:focus {
    outline: none;
  }
`;

const AuthHeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[4]};
  min-height: 72px;
  padding: 0 ${spacing[5]};
  width: 100%;
`;

const AuthHeaderBrand = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[4]};
  min-width: 0;
`;

const AuthHeaderCopy = styled.div`
  display: none;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  @media (min-width: ${breakpoints.md}) {
    display: flex;
  }
`;

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (!isAdminUser(user)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const isAuthFullBleedPage =
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/register-existing-member' ||
    location.pathname === '/register-existing-institution' ||
    location.pathname === '/register-individual' ||
    location.pathname === '/register-institution';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const portalTitle = isAdminUser(user)
    ? t('nav.portalAdmin')
    : user?.role === UserRole.InstitutionMember
      ? t('nav.portalInstitution')
      : t('nav.portalMember');

  // Wait for token bootstrap so authenticated pages don't mount twice
  // (login shell → authenticated shell) on every refresh.
  if (isLoading) {
    return <LoadingSpinner fullPage size="lg" />;
  }

  if (!isAuthenticated) {
    return (
      <AppContainer style={{ display: 'flex', flexDirection: 'column' }}>
        <Header>
          <a href="#main-content" className="skip-link">
            {t('common.skipToContent')}
          </a>
          <AuthHeaderBar>
            <AuthHeaderBrand>
              <ICOMHeaderLogo size="large" />
              <AuthHeaderCopy>
                <HeaderBrandTitle>{t('welcome.title')}</HeaderBrandTitle>
                <HeaderBrandSubtitle>{t('welcome.subtitle')}</HeaderBrandSubtitle>
              </AuthHeaderCopy>
            </AuthHeaderBrand>
            <LanguageDropdown />
          </AuthHeaderBar>
        </Header>
        <MainContent id="main-content" tabIndex={-1} style={isAuthFullBleedPage ? { padding: 0 } : undefined}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/account-help" element={<Navigate to="/forgot-password" replace />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register-individual" element={<ErrorBoundary><RegisterIndividual /></ErrorBoundary>} />
            <Route path="/register-institution" element={<ErrorBoundary><RegisterInstitution /></ErrorBoundary>} />
            <Route path="/register-existing-member" element={<ErrorBoundary><RegisterExistingMember /></ErrorBoundary>} />
            <Route path="/register-existing-institution" element={<ErrorBoundary><RegisterExistingInstitution /></ErrorBoundary>} />
            <Route path="/register-success" element={<RegisterSuccess />} />
            <Route path="/application-status" element={<ApplicationStatus />} />
            <Route path="/confirm-email" element={<EmailConfirmation />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </MainContent>
      </AppContainer>
    );
  }

  return (
    <AppContainer style={{ display: 'flex', flexDirection: 'column' }}>
      <Header>
        <HeaderContent>
          <LogoRegion>
            <MenuButton
              type="button"
              onClick={() => setMobileNavOpen(true)}
              aria-label={t('nav.openMenu')}
              aria-expanded={mobileNavOpen}
              aria-controls="mobile-nav-drawer"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </MenuButton>
            <ICOMHeaderLogo size="medium" />
          </LogoRegion>
          <HeaderBrand>
            <HeaderBrandTitle>{portalTitle}</HeaderBrandTitle>
            <HeaderBrandSubtitle>{t('welcome.subtitle')}</HeaderBrandSubtitle>
          </HeaderBrand>
          <HeaderToolbar>
            <ToolbarCluster>
              <ThemeToggle />
              <LanguageDropdown />
            </ToolbarCluster>
            {user && <UserMenu user={user} onLogout={handleLogout} />}
          </HeaderToolbar>
        </HeaderContent>
      </Header>
      <MobileNavDrawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        ariaLabel={t('nav.menu')}
        closeLabel={t('nav.closeMenu')}
      />
      <BodyLayout>
        <Sidebar>
          <Navigation />
        </Sidebar>
        <MainContent id="main-content" tabIndex={-1}>
          <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                {user && isAdminUser(user) ? (
                  <Navigate to="/admin" replace />
                ) : user?.role === UserRole.InstitutionMember ? (
                  <Navigate to="/institution" replace />
                ) : (
                  <Dashboard />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/institution"
            element={
              <ProtectedRoute>
                <InstitutionDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <Documents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/edit"
            element={
              <ProtectedRoute>
                <ProfileEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <ProtectedRoute>
                <Payments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/result"
            element={
              <ProtectedRoute>
                <PaymentResult />
              </ProtectedRoute>
            }
          />
          <Route
            path="/petitions"
            element={
              <ProtectedRoute>
                <Petitions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/change-requests"
            element={
              <ProtectedRoute>
                <UserChangeRequests />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/members/import"
            element={
              <AdminRoute>
                <ExcelImport />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/members/:id/edit"
            element={
              <AdminRoute>
                <AdminEditMember />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/members/:id"
            element={
              <AdminRoute>
                <MemberProfile />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/members"
            element={
              <AdminRoute>
                <AdminMembersList />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/institutions/:id/edit"
            element={
              <AdminRoute>
                <InstitutionProfile adminMode />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/institutions/:id"
            element={
              <AdminRoute>
                <InstitutionProfileView />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/institutions"
            element={
              <AdminRoute>
                <AdminInstitutionsList />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/change-requests"
            element={
              <AdminRoute>
                <AdminChangeRequests />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/petitions/:id"
            element={
              <AdminRoute>
                <AdminRegistrationPetitionDetail />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/petitions"
            element={
              <AdminRoute>
                <AdminRegistrationPetitions />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/vignettes"
            element={
              <AdminRoute>
                <AdminVignettes />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/email"
            element={
              <AdminRoute>
                <EmailCampaign />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/payments"
            element={
              <AdminRoute>
                <AdminPayments />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/email/history"
            element={
              <AdminRoute>
                <EmailCampaignHistory />
              </AdminRoute>
            }
          />
          <Route path="/register-success" element={<RegisterSuccess />} />
          <Route path="/application-status" element={<ApplicationStatus />} />
          <Route path="/confirm-email" element={<EmailConfirmation />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/account-help" element={<Navigate to="/forgot-password" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </MainContent>
      </BodyLayout>
    </AppContainer>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
